import AppsIcon from '@assets/icons/navbar/apps.svg?react';
import VictronLogoBlue from '@assets/victron-logo-blue.svg?react';
import { PublicMenuItem } from '@common/types/common';
import { APP_PREFIX_PATH } from '@constants/common';
import { getPublicItems } from '@constants/navigation';
import { useAuth } from '@hooks/useAuth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import {
    Avatar,
    Box,
    ButtonBase,
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Button as MuiButton,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { alpha, type Theme } from '@mui/material/styles';
import { stringAvatar } from '@utils/common';
import { pathToVP } from '@utils/route';
import { getRoleName } from '@utils/user';
import clsx from 'clsx';
import { ComponentType, useMemo, useState } from 'react';
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import type { LinkProps } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';

interface PublicNavbarProps {
    onClose?: () => void;
}

type NavbarItemProps = {
    to?: LinkProps['to'] | URL;
    disabled?: boolean;
    loading?: boolean;
    label?: MessageDescriptor;
    icon?: ComponentType<SvgIconProps>;
    iconProps?: SvgIconProps;
    onClick?: () => void;
    textProps?: Partial<React.ComponentProps<typeof ListItemText>>;
};

const getNavbarDestination = (to: LinkProps['to']): LinkProps['to'] =>
    typeof to === 'string' && !to.startsWith('/') ? `${APP_PREFIX_PATH}/${to}` : to;

const isExternalHref = (href: string) => /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);
const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/';

const getNavbarItemPath = (to: LinkProps['to'] | URL): string | undefined => {
    if (to instanceof URL || (typeof to === 'string' && isExternalHref(to))) {
        return undefined;
    }

    const destination = typeof to === 'string' ? getNavbarDestination(to) : to;
    const path = typeof destination === 'string' ? destination.split(/[?#]/, 1)[0] : destination.pathname;
    return path ? normalizePath(path) : undefined;
};

const NavbarItem = ({
    to = '/',
    label,
    icon: Icon,
    iconProps,
    disabled,
    loading,
    onClick,
    textProps,
}: NavbarItemProps) => {
    const { pathname } = useLocation();
    const targetPath = getNavbarItemPath(to);
    const currentPath = normalizePath(pathname);
    const isActive =
        targetPath === APP_PREFIX_PATH || targetPath === '/'
            ? currentPath === targetPath
            : !!targetPath && (currentPath === targetPath || currentPath.startsWith(`${targetPath}/`));

    const itemContent = (
        <>
            {Icon && (
                <ListItemIcon className="min-w-0 pr-[10px]">
                    {loading ? <Skeleton variant="circular" width={20} height={20} /> : <Icon {...iconProps} />}
                </ListItemIcon>
            )}
            <ListItemText
                {...textProps}
                slotProps={{
                    ...textProps?.slotProps,
                    primary: {
                        fontSize: 15.251,
                        lineHeight: '23.966px',
                        ...textProps?.slotProps?.primary,
                    },
                }}
            >
                {loading ? <Skeleton variant="text" width={100} /> : <FormattedMessage {...label} />}
            </ListItemText>
        </>
    );

    const itemButtonProps = {
        className: 'py-[2px] px-[10px] rounded my-[4px]',
        disabled: disabled || loading,
        onClick,
        sx: (theme: Theme) => ({
            border: `1px solid ${isActive && !loading ? alpha('#000', 0.16) : 'transparent'}`,
            bgcolor: isActive && !loading ? 'neutral-100.main' : undefined,
            transition: theme.transitions.create(['background-color', 'border-color']),
            '&:hover': {
                bgcolor: 'neutral-100.main',
                borderColor: alpha('#000', 0.16),
            },
        }),
    };

    if (to instanceof URL) {
        return (
            <ListItemButton component="a" href={to.toString()} {...itemButtonProps}>
                {itemContent}
            </ListItemButton>
        );
    }

    if (typeof to === 'string' && isExternalHref(to)) {
        return (
            <ListItemButton component="a" href={to} {...itemButtonProps}>
                {itemContent}
            </ListItemButton>
        );
    }

    return (
        <ListItemButton component={Link} to={getNavbarDestination(to)} {...itemButtonProps}>
            {itemContent}
        </ListItemButton>
    );
};

const accountItems: PublicMenuItem[] = [
    {
        label: defineMessage({
            id: 'navbar.accountOverview',
            defaultMessage: 'Account overview',
        }),
        to: `${APP_PREFIX_PATH}/accounts`,
        icon: ManageAccountsOutlinedIcon,
    },
    {
        label: defineMessage({
            id: 'navbar.logout',
            defaultMessage: 'Logout',
        }),
        to: pathToVP('/accounts/logout'),
        icon: LogoutOutlinedIcon,
    },
];

const authButtonSx = {
    borderRadius: 9999,
    fontSize: 14,
    letterSpacing: 0,
    fontWeight: 400,
    paddingTop: '4px',
    paddingBottom: '4px',
};

export const PublicNavbar = ({ onClose }: PublicNavbarProps) => {
    const [accountExpanded, setAccountExpanded] = useState(false);

    const intl = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { isLoggedIn, user, isUserLoading } = useAuth();

    const handleNavigation = () => {
        onClose?.();
        setAccountExpanded(false);
    };

    const middleItems = useMemo(
        () => [
            ...getPublicItems({ isLoggedIn, user }),
            ...(!isMobile
                ? [
                      {
                          label: defineMessage({
                              id: 'navbar.dashboard',
                              defaultMessage: 'Dashboard',
                          }),
                          to: pathToVP('dashboard'),
                          icon: AppsIcon,
                          disabled: !isLoggedIn,
                      },
                  ]
                : []),
        ],
        [intl, isLoggedIn, user]
    );

    return (
        <Stack
            component="aside"
            position="relative"
            height="100%"
            minHeight="0"
            direction="column"
            overflow="hidden"
            width={isMobile ? '100%' : '250px'}
            borderRight={isMobile ? 'none' : `1px solid ${theme.palette.divider}`}
        >
            <Box component="header" position="relative" flexShrink={0}>
                <Link to={APP_PREFIX_PATH} onClick={handleNavigation} aria-label="Victron Energy home">
                    <VictronLogoBlue
                        className={clsx('w-full cursor-pointer py-[26.5px] px-5', {
                            hidden: isMobile,
                        })}
                    />
                </Link>
            </Box>
            <List
                component="nav"
                className="min-h-0 flex-1 overflow-y-auto py-0 px-[14px]"
                sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
            >
                {middleItems.map((item) => (
                    <NavbarItem
                        key={item.label.id}
                        to={item.to}
                        label={item.label}
                        icon={item.icon}
                        disabled={item.disabled}
                        loading={isUserLoading}
                    />
                ))}
            </List>
            {isMobile ? (
                isLoggedIn ? (
                    <Stack
                        component="section"
                        mx="15px"
                        mb="10px"
                        sx={(theme) => ({
                            overflow: 'hidden',
                            border: `1px solid ${accountExpanded ? alpha('#000', 0.16) : 'transparent'}`,
                            borderRadius: '8px',
                            bgcolor: accountExpanded ? 'neutral-100.main' : undefined,
                            transition: theme.transitions.create(['background-color', 'border-color']),
                            '&:hover': {
                                bgcolor: 'neutral-100.main',
                                borderColor: alpha('#000', 0.16),
                            },
                        })}
                    >
                        <ButtonBase
                            type="button"
                            onClick={() => setAccountExpanded((expanded) => !expanded)}
                            aria-expanded={accountExpanded}
                            aria-controls="mobile-account-items"
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: '12px',
                                borderRadius: 'inherit',
                                p: '16px',
                                textAlign: 'left',
                                bgcolor: 'transparent',
                                '&:hover': {
                                    bgcolor: 'transparent',
                                },
                            }}
                        >
                            <Avatar
                                {...stringAvatar(
                                    user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : ''
                                )}
                                variant="circular"
                                sx={{
                                    width: 46,
                                    height: 46,
                                }}
                            />
                            <Box maxWidth={{ md: 100, lg: 300 }} textAlign="left" flex={1} minWidth={0}>
                                <Typography
                                    fontFamily="museo-sans"
                                    fontSize={16}
                                    fontWeight={600}
                                    letterSpacing={-0.32}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    whiteSpace="nowrap"
                                >
                                    {user?.first_name && user?.last_name
                                        ? `${user.first_name} ${user.last_name}`
                                        : intl.formatMessage({ id: 'common.userName', defaultMessage: 'User Name' })}
                                </Typography>
                                {user?.role && (
                                    <Typography
                                        fontFamily="museo-sans"
                                        fontSize={12}
                                        color="neutral-900"
                                        letterSpacing={-0.24}
                                        textTransform="uppercase"
                                        fontWeight={300}
                                    >
                                        {getRoleName(user.role, intl)}
                                    </Typography>
                                )}
                            </Box>
                            <ExpandMoreIcon
                                sx={{
                                    transition: (theme) => theme.transitions.create('transform'),
                                    transform: accountExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        </ButtonBase>
                        <Collapse in={accountExpanded} timeout="auto" unmountOnExit>
                            <List id="mobile-account-items" component="nav" className="px-[6px] py-0">
                                {accountItems.map((item) => (
                                    <NavbarItem
                                        key={item.label.id}
                                        to={item.to}
                                        label={item.label}
                                        onClick={handleNavigation}
                                        textProps={{
                                            slotProps: {
                                                primary: {
                                                    color: 'neutral-800',
                                                    fontSize: 14,
                                                    fontWeight: 400,
                                                },
                                            },
                                        }}
                                    />
                                ))}
                            </List>
                        </Collapse>
                    </Stack>
                ) : (
                    <Box
                        component="section"
                        overflow="hidden"
                        borderRadius="8px"
                        bgcolor="neutral-100.main"
                        p="16px"
                        mx="15px"
                        mb="10px"
                        sx={(theme) => ({
                            border: `1px solid ${alpha('#000', 0.16)}`,
                            transition: theme.transitions.create(['background-color', 'border-color']),
                            '&:hover': {
                                bgcolor: 'neutral-100.main',
                                borderColor: alpha('#000', 0.16),
                            },
                        })}
                    >
                        <Typography
                            fontFamily="museo-sans"
                            fontSize={16}
                            color="neutral-900"
                            fontWeight={600}
                            letterSpacing={-0.32}
                        >
                            <FormattedMessage id="navbar.welcome" defaultMessage="Welcome to Victron Professional" />
                        </Typography>
                        <Typography fontFamily="museo-sans" fontSize={13} color="#53575A" fontWeight={300}>
                            <FormattedMessage
                                id="navbar.welcome"
                                defaultMessage="Log in to access what Professional has to offer."
                            />
                        </Typography>
                        <Stack direction="row" spacing={1} mt="14px">
                            <MuiButton
                                variant="contained"
                                color="primary"
                                size="small"
                                component={Link}
                                to={`${APP_PREFIX_PATH}/auth/login`}
                                className="black-border"
                                sx={authButtonSx}
                            >
                                Login
                            </MuiButton>
                            <MuiButton
                                variant="outlined"
                                color="neutral-800"
                                size="small"
                                component={Link}
                                to={`${APP_PREFIX_PATH}/auth/sign-up`}
                                className="black-border"
                                sx={(theme) => ({
                                    ...authButtonSx,
                                    borderColor: theme.palette['neutral-450'].main,
                                })}
                            >
                                Register
                            </MuiButton>
                        </Stack>
                    </Box>
                )
            ) : null}
        </Stack>
    );
};
